import { useCallback, useContext } from "react";
import { UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./InvoiceAccounting.module.css";
import { IAccounting } from "src/stores/krediflow/InvoiceTypes";
import { ErpType } from "src/network/User";
import { RealestateField } from "./RealestateField";
import { AccountField } from "./AccountField";
import { CostcenterField } from "./CostcenterField";
import { BookingTextField } from "./BookingTextField";
import { AmountField } from "./AmountField";
import { QuantityField } from "./QuantityField";
import { ExtraCostDateField } from "./ExtraCostDateField";
import { VatCodeField } from "./VatCodeField";
import { VatRateField } from "./VatRateField";
import { NetAmountField } from "./NetAmountField";
import { UnitField } from "./UnitField";
import { VatProportionField } from "./VatProportionField";
import { VatProportionHint } from "./VatProportionHint";

interface InvoiceAccountingProperties {
    accounting: IAccounting;
    accountingIndex: number;
}

const InvoiceAccountingBase = (props: InvoiceAccountingProperties) => {
    const { invoiceStore, authStore, accountingAccountSearchStore } = useContext(RootStoreContext);

    const { accountingIndex } = props;

    const removeAccountingRow = useCallback(
        (index: number) => () => {
            invoiceStore.currentInvoice?.removeAccountingRow(index);
        },
        [invoiceStore]
    );

    const onDeleteKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                removeAccountingRow(accountingIndex)();
            }
        },
        [removeAccountingRow, accountingIndex]
    );

    const accountField = <AccountField accountingRowIndex={accountingIndex} />;
    const costCenterField = <CostcenterField accountingRowIndex={accountingIndex} />;
    const bookingTextField = <BookingTextField accountingRowIndex={accountingIndex} />;

    const amountField = <AmountField accountingRowIndex={accountingIndex} />;

    const removeAccountingRowIcon =
        accountingIndex > 0 ? (
            <UI.Icon
                icon={UI.SVGIcon.Trash}
                size={30}
                className={`ml-2 ${styles.DeleteIcon}`}
                onClick={removeAccountingRow(accountingIndex)}
                tabIndex={0}
                onKeyDown={onDeleteKeyDown}
            />
        ) : (
            <></>
        );

    const quantityField = <QuantityField accountingRowIndex={accountingIndex} />;
    const extraCostDateField = <ExtraCostDateField accountingRowIndex={accountingIndex} />;
    const unitField = <UnitField accountingRowIndex={accountingIndex} />;
    const vatCodeField = <VatCodeField accountingRowIndex={accountingIndex} />;
    const vatRateField = <VatRateField accountingRowIndex={accountingIndex} />;
    const netAmountField = <NetAmountField accountingRowIndex={accountingIndex} />;
    const vatProportionField = <VatProportionField accountingRowIndex={accountingIndex} />;

    // #24071: the share takes the 2 columns of the spacer so the second row stays at 12 columns.
    const showVatProportion = invoiceStore.currentInvoice?.canSeeAccountingVatProportionField(accountingIndex) ?? false;

    const erpType = authStore.user?.erpType;

    const accountingRowTwoIT2 = (
        <>
            <UI.Col
                lg={invoiceStore.currentInvoice?.canEditExtraCostDateField(accountingIndex) ? 2 : 0}
                className={`ExtraCostDateColumn ${
                    invoiceStore.currentInvoice?.canEditExtraCostDateField(accountingIndex) ? "" : "d-none"
                }`}
            >
                {extraCostDateField}
            </UI.Col>
            <UI.Col
                lg={invoiceStore.currentInvoice?.canEditExtraCostDateField(accountingIndex) ? 2 : 4}
                className={`AccountingQuantityColumn ${
                    invoiceStore.currentInvoice?.canEditAccountingQuantityField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
            >
                {quantityField}
            </UI.Col>
            <UI.Col
                lg={showVatProportion ? 0 : 2}
                className={`AccountingNetAmountSpacingColumn ${showVatProportion ? "d-none" : ""}`}
            >
                <span
                    className={`${
                        invoiceStore.currentInvoice?.canEditAccountingQuantityField(accountingIndex)
                            ? styles.VatAmountText
                            : "invisible"
                    }`}
                ></span>
            </UI.Col>
            <UI.Col
                className={`AccountingVatCodeColumn ${
                    invoiceStore.currentInvoice?.canSeeAccountingVatCodeField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
                lg={2}
            >
                {vatCodeField}
            </UI.Col>
            <UI.Col
                className={`AccountingVatRateColumn ${
                    invoiceStore.currentInvoice?.canSeeAccountingVatCodeField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
                lg={2}
            >
                {vatRateField}
            </UI.Col>

            <UI.Col
                lg={showVatProportion ? 2 : 0}
                className={`AccountingVatProportionColumn ${showVatProportion ? "d-flex align-items-end" : "d-none"}`}
            >
                {vatProportionField}
            </UI.Col>
            <UI.Col
                lg={2}
                className={`AccountingNetAmountColumn ${
                    invoiceStore.currentInvoice?.canSeeAccountingNetAmountField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
            >
                <div className="d-flex w-100">
                    <div style={{ width: "84%" }}>{netAmountField}</div>
                    <div style={{ width: "16%" }}></div>
                </div>
            </UI.Col>
        </>
    );

    const accountingRowTwoRimo = (
        <>
            <UI.Col lg={3} className={"UnitColumn"}>
                {unitField}
            </UI.Col>
            <UI.Col
                lg={invoiceStore.currentInvoice?.canEditExtraCostDateField(accountingIndex) ? 2 : 0}
                className={`ExtraCostDateColumn ${
                    invoiceStore.currentInvoice?.canEditExtraCostDateField(accountingIndex) ? "" : "d-none"
                }`}
            >
                {extraCostDateField}
            </UI.Col>
            <UI.Col
                lg={invoiceStore.currentInvoice?.canEditExtraCostDateField(accountingIndex) ? 2 : 4}
                className={`AccountingQuantityColumn ${
                    invoiceStore.currentInvoice?.canEditAccountingQuantityField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
            >
                {quantityField}
            </UI.Col>
            <UI.Col
                lg={showVatProportion ? 0 : 2}
                className={`AccountingNetAmountSpacingColumn ${showVatProportion ? "d-none" : ""}`}
            >
                <span
                    className={`${
                        invoiceStore.currentInvoice?.canEditAccountingQuantityField(accountingIndex)
                            ? styles.VatAmountText
                            : "invisible"
                    }`}
                ></span>
            </UI.Col>
            <UI.Col
                className={`AccountingVatCodeColumn ${
                    invoiceStore.currentInvoice?.canSeeAccountingVatCodeField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
                lg={2}
            >
                {vatCodeField}
            </UI.Col>
            <UI.Col
                className={`AccountingVatRateColumn ${
                    invoiceStore.currentInvoice?.canSeeAccountingVatCodeField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
                lg={2}
            >
                {vatRateField}
            </UI.Col>

            <UI.Col
                lg={showVatProportion ? 2 : 0}
                className={`AccountingVatProportionColumn ${showVatProportion ? "d-flex align-items-end" : "d-none"}`}
            >
                {vatProportionField}
            </UI.Col>
            <UI.Col
                lg={2}
                className={`AccountingNetAmountColumn ${
                    invoiceStore.currentInvoice?.canSeeAccountingNetAmountField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
            >
                <div className="d-flex w-100">
                    <div style={{ width: "84%" }}>{netAmountField}</div>
                    <div style={{ width: "16%" }}></div>
                </div>
            </UI.Col>
        </>
    );

    const accountingColumnsIT2 = (
        <>
            {/* First Row (12 Columns) */}
            <UI.Col
                className={
                    accountingAccountSearchStore.areAccountTagsDisplayedByIndex[accountingIndex]
                        ? styles.ExtraPadding
                        : ""
                }
                lg={invoiceStore.currentInvoice?.canEditAccountingCostcenterField(accountingIndex) ? 3 : 6}
            >
                {accountField}
            </UI.Col>
            <UI.Col
                className={
                    invoiceStore.currentInvoice?.canEditAccountingCostcenterField(accountingIndex) ? "" : "d-none"
                }
                lg={3}
            >
                {costCenterField}
            </UI.Col>
            <UI.Col
                lg={4}
                style={{
                    marginBottom: invoiceStore.currentInvoice?.hasBookingTextNewline(accountingIndex) ? "20px" : ""
                }}
            >
                {bookingTextField}
            </UI.Col>
            <UI.Col lg={2}>
                <div className="d-flex">
                    <div style={{ width: "84%" }}>{amountField}</div>
                    <div className="d-flex align-items-center" style={{ width: "16%" }}>
                        {removeAccountingRowIcon}
                    </div>
                </div>
            </UI.Col>

            {/* Second Row (12 Columns) */}
            {accountingRowTwoIT2}
        </>
    );

    const accountingColumnsRIMO = (
        <>
            {/* First Row (12 Columns) */}
            <UI.Col lg={3}>{<RealestateField accountingRowIndex={accountingIndex} />}</UI.Col>
            <UI.Col
                className={
                    accountingAccountSearchStore.areAccountTagsDisplayedByIndex[accountingIndex]
                        ? styles.ExtraPadding
                        : ""
                }
                lg={3}
            >
                {accountField}
            </UI.Col>
            <UI.Col
                lg={4}
                style={{
                    marginBottom: invoiceStore.currentInvoice?.hasBookingTextNewline(accountingIndex) ? "20px" : ""
                }}
            >
                {bookingTextField}
            </UI.Col>
            <UI.Col lg={2}>
                <div className="d-flex">
                    <div style={{ width: "84%" }}>{amountField}</div>
                    <div className="d-flex align-items-center" style={{ width: "16%" }}>
                        {removeAccountingRowIcon}
                    </div>
                </div>
            </UI.Col>

            {/* Second Row (12 Columns) */}
            {accountingRowTwoRimo}
        </>
    );

    const accountingValuationErrorString =
        invoiceStore.currentInvoice?.getAccountingValuationErrorString(accountingIndex);

    const isMissingRequiredCostcenterDataErrorString =
        invoiceStore.currentInvoice?.getAccountingIsMissingRequiredCostCenterDataErrorString(accountingIndex);

    const accountingValuationRealestateMismatchErrorString =
        invoiceStore.currentInvoice?.getAccountingValuationRealestateMismatchErrorString(accountingIndex);

    const vatProportionErrorString =
        invoiceStore.currentInvoice?.getAccountingVatProportionErrorString(accountingIndex);

    return (
        <>
            <UI.Row className={`${styles.AccountingRowContainer} ${accountingIndex > 0 ? "mt-2" : ""}`}>
                {erpType === ErpType.IT2
                    ? accountingColumnsIT2
                    : erpType === ErpType.RIMO
                      ? accountingColumnsRIMO
                      : undefined}
            </UI.Row>
            {accountingValuationErrorString ? (
                <div style={{ textAlign: "right" }}>
                    <span className="error-text">{accountingValuationErrorString}</span>
                </div>
            ) : (
                ""
            )}
            {isMissingRequiredCostcenterDataErrorString ? (
                <div style={{ textAlign: "right" }}>
                    <span className="error-text">{isMissingRequiredCostcenterDataErrorString}</span>
                </div>
            ) : (
                ""
            )}
            {accountingValuationRealestateMismatchErrorString ? (
                <div style={{ textAlign: "right" }}>
                    <span className="error-text">{accountingValuationRealestateMismatchErrorString}</span>
                </div>
            ) : (
                ""
            )}
            {vatProportionErrorString ? (
                <div style={{ textAlign: "right" }}>
                    <span className="error-text">{vatProportionErrorString}</span>
                </div>
            ) : (
                ""
            )}
            {showVatProportion ? <VatProportionHint /> : ""}
        </>
    );
};

export const InvoiceAccounting = Sentry.withProfiler(observer(InvoiceAccountingBase));
